import { useCallback, useRef, useState } from 'react';
import { Modal } from 'flowbite-react';
import { HiOutlineRocketLaunch } from "react-icons/hi2";
import { publish } from '../../services/app/app-service';
import TextBox from '../forms/text-box';
import TextArea from '../forms/text-area';
import Button from '../button/button';

function PublishDialog({ appId, onCompletion, reviewRequest = null }) {
    const rootRef = useRef(null);
    const [formData, setFormData] = useState({
        versionName: reviewRequest?.versionRequest?.versionName || '',
        releaseNote: reviewRequest?.versionRequest?.releaseNote || ''
    });
    const [isPublishing, setIsPublishing] = useState(false);
    const [isFromReviewRequest, setIsFromReviewRequest] = useState(!!reviewRequest);
    const cancelButtonRef = useRef(null);

    const handleChange = useCallback(event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;
        formData[targetName] = targetValue;
        setFormData(Object.assign({}, formData));
    });

    const handleOnCreate = async () => {
        if (!formData.versionName.trim()) {
            return;
        }
        
        setIsPublishing(true);
        const { status } = await publish(
            appId, 
            formData.versionName, 
            formData.releaseNote, 
            isFromReviewRequest
        );
        setIsPublishing(false);
        
        if (status === 200) {
            onCompletion(true, "Successfully published changes");
        } else {
            onCompletion(false, "Unable to publish changes");
        }
    };

    const handleOnCancel = useCallback(() => {
        if (!isPublishing) {
            onCompletion(false);
        }
    });

    return (
        <div ref={rootRef}>
        <Modal show={true} size="md" popup onClose={handleOnCancel} root={rootRef.current ?? undefined}>
            <Modal.Header>
                <span className="text-lg font-semibold text-gray-900 pl-4 pt-2">
                    Publish to Live
                </span>
            </Modal.Header>
            <Modal.Body>
                <div className="space-y-4">
                    <div className="text-center mb-4">
                        <HiOutlineRocketLaunch className="mx-auto mb-2 h-12 w-12 text-indigo-500 dark:text-indigo-400" />
                        <p className="text-sm text-gray-500">
                            Create a new version to publish your changes
                        </p>
                    </div>
                    
                    <TextBox
                        id="versionName"
                        name="versionName"
                        label="Version Name *"
                        type="text"
                        placeholder="e.g., v1.0.0 or Release 1"
                        value={formData.versionName}
                        onChange={handleChange}
                        disabled={isPublishing}
                    />
                    
                    <TextArea
                        id="releaseNote"
                        name="releaseNote"
                        label="Release Note"
                        placeholder="Describe what's new in this release..."
                        rows={4}
                        value={formData.releaseNote}
                        onChange={handleChange}
                        disabled={isPublishing}
                    />
                    
                    <div className="flex justify-end gap-3 pt-2">
                        <Button 
                            color="default" 
                            text="Cancel"
                            onClick={handleOnCancel} 
                            disabled={isPublishing}
                        />
                        <Button 
                            color="primary" 
                            text={isPublishing ? "Publishing..." : "Create"}
                            onClick={handleOnCreate}
                            disabled={!formData.versionName.trim() || isPublishing}
                            loading={isPublishing}
                        />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
        </div>
    );
}

export default PublishDialog;
